import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

type Language = 'ru' | 'uz' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

/** Extract language from pathname first segment. Returns null if not a lang prefix. */
function getLangFromPath(pathname: string): Language | null {
  const seg = pathname.split('/')[1];
  if (seg === 'ru' || seg === 'en' || seg === 'uz') return seg;
  return null;
}

const SUPPORTED_LANGS: Language[] = ['ru', 'en', 'uz'];

// Dictionary lives in src/lib/i18n/dictionary.ts so this file stays small
// and so HMR / IDE / build can handle it efficiently.
import { translations } from '@/lib/i18n/dictionary';


const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function detectBrowserLanguage(): Language {
  if (typeof navigator === 'undefined') return 'en';

  const raw = (navigator.languages && navigator.languages.length > 0
    ? navigator.languages[0]
    : navigator.language
  )
    ?.toLowerCase()
    .trim();

  if (raw?.startsWith('ru')) return 'ru';
  if (raw?.startsWith('uz')) return 'uz';
  return 'en';
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // URL is the primary source of truth — safe to read window here (no router needed)
    const fromUrl = getLangFromPath(
      typeof window !== 'undefined' ? window.location.pathname : '/'
    );
    if (fromUrl) return fromUrl;
    // Fallback: localStorage, then browser lang
    const saved = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
    return (saved as Language) || detectBrowserLanguage();
  });

  // Sync language on browser navigation (popstate covers back/forward + react-router pushState)
  // Does NOT use useLocation — safe to run above <Router>
  useEffect(() => {
    const syncFromUrl = () => {
      const fromUrl = getLangFromPath(window.location.pathname);
      if (fromUrl) {
        setLanguageState(prev => (fromUrl !== prev ? fromUrl : prev));
      }
    };

    // popstate fires on back/forward navigation
    window.addEventListener('popstate', syncFromUrl);

    // Patch history.pushState / replaceState so SPA navigations also trigger sync
    const origPush = history.pushState.bind(history);
    const origReplace = history.replaceState.bind(history);

    history.pushState = (...args) => {
      origPush(...args);
      syncFromUrl();
    };
    history.replaceState = (...args) => {
      origReplace(...args);
      syncFromUrl();
    };

    return () => {
      window.removeEventListener('popstate', syncFromUrl);
      history.pushState = origPush;
      history.replaceState = origReplace;
    };
  }, []);

  // Persist to localStorage & update <html lang>
  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  // setLanguage is kept for compatibility but URL navigation is the canonical way
  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const t = (key: string) => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};
